/**
 * KnowledgeBaseArticle - Main article component using modular structure
 * 
 * PROJECT_RULES.md Compliance:
 * - ✅ Mobile: Safe area handling
 * - ✅ Consistent design language  
 * - ✅ TypeScript: Fully typed
 * - ✅ File size: Under 250 lines
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import LandingHeader from '../LandingHeader';
import Footer from '../../../components/Footer';
import { isIOSDevice } from '../../utils/deviceDetection';
import { useArticleLoader } from './hooks';
import { getAccentColor } from './utils';
import { BACKGROUND_COLORS } from './constants';
import {
  LoadingState,
  ErrorState,
  Breadcrumb,
  ArticleContent,
  ArticleNavigation
} from './components';

const KnowledgeBaseArticle: React.FC = () => {
  const { category, article } = useParams<{ category: string; article: string }>();
  const { articleData, loading, error } = useArticleLoader(category, article);
  const isIOS = isIOSDevice();

  if (loading) {
    return <LoadingState />;
  }

  if (error || !articleData) {
    return <ErrorState />;
  }

  const accentColor = getAccentColor(articleData.category);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: BACKGROUND_COLORS.main }}>
      <LandingHeader />
      <div 
        className="flex-grow container mx-auto px-4 py-8 max-w-4xl"
        style={{ 
          marginTop: isIOS ? 'calc(4rem + env(safe-area-inset-top, 0px))' : '4rem'
        }}
      >
        <Breadcrumb category={articleData.category} title={articleData.title} />
        <ArticleContent articleData={articleData} accentColor={accentColor} />
        <ArticleNavigation 
          category={articleData.category} 
          article={article || ''} 
          accentColor={accentColor} 
        />
      </div>
      <Footer />
    </div>
  );
};

export default KnowledgeBaseArticle;